import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, DatePicker, Form, Input, Popconfirm, Select, Table, Tag, message } from "antd";
import { collection, doc, onSnapshot, setDoc, updateDoc } from "firebase/firestore";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { FinanceAssetType, FinanceEntryType } from "../typeDefs";

export const FinanceAssetContainers = ({ data }: { data: FinanceEntryType[] }) => {
  const [form] = Form.useForm();
  const [assets, setAssets] = useState<FinanceAssetType[]>([]);
  const [saving, setSaving] = useState(false);
  const [entrySearch, setEntrySearch] = useState("");
  const [entryDateRange, setEntryDateRange] = useState<[number, number] | null>(null);
  const { RangePicker } = DatePicker;

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "financeAssets"), (snapshot) => {
      setAssets(
        snapshot.docs
          .map((snapDoc) => ({ id: snapDoc.id, ...(snapDoc.data() as Omit<FinanceAssetType, "id">) }))
          .filter((asset) => !asset.isDeleted)
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)),
      );
    });

    return unsub;
  }, []);

  const financeAssetOptions = assets.map((asset) => ({ value: asset.id, label: asset.name }));

  const assignableEntries = useMemo(
    () =>
      data
        .filter((entry) => !entry.isDeleted)
        .filter((entry) => entry.category === "Expenses" || entry.category === "Assets")
        .sort((a, b) => b.date - a.date),
    [data],
  );

  const filteredAssignableEntries = useMemo(() => {
    const search = entrySearch.trim().toLowerCase();
    return assignableEntries.filter((entry) => {
      const matchesDate =
        !entryDateRange || (entry.date >= entryDateRange[0] && entry.date <= entryDateRange[1]);
      const searchableText = [
        entry.vendor,
        entry.description,
        entry.category,
        entry.subCategory,
        entry.financeAssetName,
        entry.amount?.toString(),
        new Date(entry.date).toLocaleDateString("en-GB"),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return matchesDate && (!search || searchableText.includes(search));
    });
  }, [assignableEntries, entryDateRange, entrySearch]);

  const assetStats = assets.map((asset) => {
    const childEntries = assignableEntries.filter((entry) => entry.financeAssetId === asset.id);
    const total = childEntries.reduce((sum, entry) => sum + entry.amount, 0);
    return {
      ...asset,
      childCount: childEntries.length,
      total,
    };
  });

  const createAsset = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const id = nanoid();
      await setDoc(doc(db, `financeAssets/${id}`), {
        id,
        name: values.name,
        description: values.description || "",
        status: values.status || "Active",
        createdAt: Date.now(),
        isDeleted: false,
      } as FinanceAssetType);
      form.resetFields();
      message.success("Finance asset container created.");
    } catch (err) {
      console.error(err);
      message.error("Could not create asset container.");
    } finally {
      setSaving(false);
    }
  };

  const assignEntry = async (entry: FinanceEntryType, assetId?: string) => {
    const asset = assets.find((item) => item.id === assetId);
    await updateDoc(doc(db, `finances/${entry.id}`), {
      financeAssetId: asset?.id || null,
      financeAssetName: asset?.name || null,
      financeAssetAssignedAt: asset ? Date.now() : null,
    } as Partial<FinanceEntryType>);
    message.success(asset ? `Assigned to ${asset.name}.` : "Removed from asset container.");
  };

  const deleteAsset = async (asset: FinanceAssetType) => {
    const childEntries = assignableEntries.filter((entry) => entry.financeAssetId === asset.id);
    await Promise.all(
      childEntries.map((entry) =>
        updateDoc(doc(db, `finances/${entry.id}`), {
          financeAssetId: null,
          financeAssetName: null,
          financeAssetAssignedAt: null,
        } as Partial<FinanceEntryType>),
      ),
    );
    await updateDoc(doc(db, `financeAssets/${asset.id}`), {
      isDeleted: true,
      deletedAt: Date.now(),
    } as Partial<FinanceAssetType>);
    message.success("Asset container deleted and child entries unassigned.");
  };

  return (
    <div className="space-y-6">
      <section className="border border-white/10 bg-white/[0.02] p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">{"// asset.container.create"}</p>
        <h3 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-[#F6B34B]">Create Figurative Asset</h3>
        <p className="mt-2 max-w-3xl text-sm text-gray-300">
          Use these as parent containers for related finance entries. Example: create “Camera focus upgrade”, then assign
          focus motor, rails, cables, and accessories as child expenses.
        </p>
        <Form form={form} layout="vertical" className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_160px_auto] md:items-end">
          <Form.Item name="name" label="Asset container name" rules={[{ required: true, message: "Enter a name" }]}>
            <Input placeholder="Camera focus upgrade" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input placeholder="Optional notes" />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="Active">
            <Select options={["Active", "Complete", "Deferred", "Review"].map((value) => ({ value, label: value }))} />
          </Form.Item>
          <Button type="primary" icon={<PlusOutlined />} loading={saving} onClick={createAsset}>
            Create
          </Button>
        </Form>
      </section>

      <section className="border border-white/10 bg-white/[0.02] p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">{"// asset.container.summary"}</p>
        <h3 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-white">Asset Containers</h3>
        <Table
          className="mt-4"
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={assetStats}
          columns={[
            { title: "Asset", dataIndex: "name" },
            { title: "Description", dataIndex: "description" },
            { title: "Status", dataIndex: "status", render: (status) => <Tag color="gold">{status || "Active"}</Tag> },
            { title: "Children", dataIndex: "childCount" },
            { title: "Total", dataIndex: "total", render: (total: number) => `$${total.toFixed(2)}` },
            {
              title: "Delete",
              render: (_value, asset) => (
                <Popconfirm title="Delete this asset container? Child entries will be unassigned." onConfirm={() => deleteAsset(asset)}>
                  <Button danger type="text" icon={<DeleteOutlined />} />
                </Popconfirm>
              ),
            },
          ]}
        />
      </section>

      <section className="border border-white/10 bg-white/[0.02] p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">{"// asset.container.children"}</p>
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h3 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-white">Assign Finance Entries</h3>
            <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-[#5C6B73]">
              {filteredAssignableEntries.length} of {assignableEntries.length} assignable entries shown
            </p>
          </div>
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <Input.Search
              allowClear
              placeholder="Search vendor, description, category, amount"
              value={entrySearch}
              onChange={(event) => setEntrySearch(event.target.value)}
              style={{ minWidth: 320 }}
            />
            <RangePicker
              onChange={(range) => {
                setEntryDateRange(
                  range
                    ? [range[0]?.startOf("day").toDate().getTime() || 0, range[1]?.endOf("day").toDate().getTime() || 0]
                    : null,
                );
              }}
            />
            <Button
              onClick={() => {
                setEntrySearch("");
                setEntryDateRange(null);
              }}
            >
              Clear
            </Button>
          </div>
        </div>
        <Table
          className="mt-4"
          size="small"
          rowKey="id"
          dataSource={filteredAssignableEntries}
          columns={[
            { title: "Date", dataIndex: "date", render: (date) => new Date(date).toLocaleDateString("en-GB") },
            { title: "Vendor", dataIndex: "vendor" },
            { title: "Description", dataIndex: "description" },
            { title: "Category", render: (_value, entry) => `${entry.category} / ${entry.subCategory}` },
            { title: "Amount", dataIndex: "amount", render: (amount: number) => `$${amount.toFixed(2)}` },
            {
              title: "Asset Container",
              render: (_value, entry) => (
                <Select
                  allowClear
                  showSearch
                  placeholder="Assign to asset"
                  style={{ minWidth: 240 }}
                  value={entry.financeAssetId || undefined}
                  options={financeAssetOptions}
                  optionFilterProp="label"
                  onChange={(assetId) => assignEntry(entry, assetId)}
                />
              ),
            },
          ]}
        />
      </section>
    </div>
  );
};
